import { useState, useEffect, useRef } from "react";
import "./TermsAndConditionsPage.css";

const API = process.env.REACT_APP_API_URL || "";

const TermsAndConditionsPage = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [activeSection, setActiveSection] = useState(0);
    const [readProgress, setReadProgress] = useState(0);
    const sectionsRef = useRef([]);
    const contentRef = useRef(null);

    useEffect(() => {
        const fetchTerms = async () => {
            try {
                const res = await fetch(`${API}/api/legal/active?type=terms`);
                const json = await res.json();
                if (!res.ok) throw new Error(json.message || "Failed to load");
                setData(json.data);
            } catch (e) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        };
        fetchTerms();
    }, []);

    // Read progress bar
    useEffect(() => {
        const handleScroll = () => {
            const el = document.documentElement;
            const scrolled = el.scrollTop;
            const total = el.scrollHeight - el.clientHeight;
            setReadProgress(total > 0 ? (scrolled / total) * 100 : 0);
        };
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Active section tracker
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const idx = sectionsRef.current.indexOf(entry.target);
                        if (idx !== -1) setActiveSection(idx);
                    }
                });
            },
            { threshold: 0.3 }
        );
        sectionsRef.current.forEach((el) => el && observer.observe(el));
        return () => observer.disconnect();
    }, [data]);

    const parseSections = (content) => {
        if (!content) return [];
        const lines = content.split("\n");
        const sections = [];
        let current = null;

        lines.forEach((line) => {
            const isHeading =
                /^#{1,3}\s/.test(line) || /^\d+\.\s[A-Z]/.test(line);
            if (isHeading) {
                if (current) sections.push(current);
                current = {
                    title: line.replace(/^#{1,3}\s/, "").replace(/^\d+\.\s/, ""),
                    body: [],
                };
            } else if (current) {
                current.body.push(line);
            } else {
                if (!sections.length || sections[0].title)
                    sections.push({ title: "", body: [line] });
                else sections[0].body.push(line);
            }
        });
        if (current) sections.push(current);
        return sections.filter((s) => s.title || s.body.join("").trim());
    };

    if (loading) {
        return (
            <div className="tc-loader">
                <div className="tc-loader__ring" />
            </div>
        );
    }

    if (error) {
        return <div className="tc-error"><p>{error}</p></div>;
    }

    const sections = parseSections(data?.content);
    const updatedDate = data?.updatedAt
        ? new Date(data.updatedAt).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "long",
            year: "numeric",
        })
        : "";

    return (
        <div className="tc-root">
            {/* Progress bar */}
            <div
                className="tc-progress"
                style={{ width: `${readProgress}%` }}
                aria-hidden="true"
            />

            {/* Hero */}
            <header className="tc-hero">
                <div className="tc-hero__tag">Terms &amp; Conditions</div>
                <h1 className="tc-hero__title">
                    <span>Our</span>
                    <em> Agreement</em>
                </h1>
                <div className="tc-hero__sub">
                    {data?.version && <span>v{data.version}</span>}
                    {updatedDate && <span>Effective {updatedDate}</span>}
                </div>
                <div className="tc-hero__deco" aria-hidden="true">
                    <span /><span /><span />
                </div>
            </header>

            <div className="tc-layout" ref={contentRef}>
                {/* Sidebar */}
                {sections.length > 0 && (
                    <aside className="tc-sidebar">
                        <p className="tc-sidebar__label">In this document</p>
                        {sections
                            .filter((s) => s.title)
                            .map((s, i) => (
                                <button
                                    key={i}
                                    className={`tc-sidebar__item ${activeSection === i ? "tc-sidebar__item--active" : ""}`}
                                    onClick={() =>
                                        sectionsRef.current[i]?.scrollIntoView({ behavior: "smooth" })
                                    }
                                >
                                    <span className="tc-sidebar__bar" />
                                    <span className="tc-sidebar__text">{s.title}</span>
                                </button>
                            ))}
                    </aside>
                )}

                {/* Content */}
                <main className="tc-main">
                    {sections.length === 0 ? (
                        <p className="tc-empty">No content available.</p>
                    ) : (
                        sections.map((section, i) => (
                            <article
                                key={i}
                                id={`tc-section-${i}`}
                                className="tc-section"
                                ref={(el) => (sectionsRef.current[i] = el)}
                            >
                                {section.title && (
                                    <header className="tc-section__header">
                                        <span className="tc-section__index">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <h2 className="tc-section__title">{section.title}</h2>
                                    </header>
                                )}
                                <div className="tc-section__body">
                                    {section.body
                                        .filter((l) => l.trim())
                                        .map((line, j) => (
                                            <p key={j}>{line}</p>
                                        ))}
                                </div>
                            </article>
                        ))
                    )}

                    <footer className="tc-foot">
                        <div className="tc-foot__inner">
                            <p>
                                By using our services, you agree to these terms. Questions?{" "}
                                <a href="mailto:legal@aviatricks.in">support@aviatricks.in</a>
                            </p>
                        </div>
                    </footer>
                </main>
            </div>
        </div>
    );
};

export default TermsAndConditionsPage;